import React, { useState } from "react";
import { Check } from "lucide-react";
import DocSidebar from "../components/DocSidebar";
import CodeBlock from "../components/CodeBlock";
import MethodBadge from "../components/MethodBadge";
import Keyword from "../components/Keyword";

const sidebarItems = [
  { id: "ttt-overview-nav", label: "Getting Started", type: "category" },
  { id: "ttt-overview", label: "Overview", type: "item", indent: true },

  { id: "ttt-getting-started", label: "Getting Started", type: "heading" },
  { id: "ttt-base-url", label: "Base URL", type: "item", indent: true },
  { id: "ttt-get-api-key", label: "How To Get An API Key", type: "item", indent: true },
  { id: "ttt-auth-header", label: "Authentication Header", type: "item", indent: true },

  { id: "ttt-translation-api", label: "Translation API", type: "heading" },
  { id: "ttt-endpoint", label: "Endpoint", type: "item", indent: true },
  { id: "ttt-full-endpoint-url", label: "Full Endpoint URL", type: "item", indent: true },
  { id: "ttt-request-header", label: "Request Header", type: "item", indent: true },
  { id: "ttt-request-body", label: "Request Body (Payload)", type: "item", indent: true },
  { id: "ttt-payload-examples", label: "Payload Examples By Language Pairs", type: "item", indent: true },

  { id: "ttt-language-reference", label: "Language Reference", type: "heading" },
  { id: "ttt-supported-values", label: "Supported Language Values", type: "item", indent: true },
  { id: "ttt-pair-rules", label: "Language Pair Rules", type: "item", indent: true },
  { id: "ttt-valid-pairs", label: "Valid Language Pairs", type: "item", indent: true },

  { id: "ttt-code-examples", label: "Code Examples", type: "heading" },
  { id: "ttt-python", label: "Python", type: "item", indent: true },
  { id: "ttt-curl", label: "cURL", type: "item", indent: true },
  { id: "ttt-javascript", label: "JavaScript (Fetch)", type: "item", indent: true },

  { id: "ttt-response-format", label: "Response Format", type: "heading" },
  { id: "ttt-success-response", label: "Success Response", type: "item", indent: true },
  { id: "ttt-error-responses", label: "Error Responses", type: "item", indent: true },

  { id: "ttt-best-practices", label: "Best Practices", type: "heading" },
  { id: "ttt-next-steps", label: "Next Steps", type: "heading" },
];

const TextToTextTab = () => {
  const [activeSection, setActiveSection] = useState("ttt-overview");

  return (
    <div className="flex flex-col lg:flex-row gap-8 max-w-[1440px] mx-auto px-2 sm:px-6 py-6 sm:py-10 relative">
      {/* Sidebar Navigation */}
      <DocSidebar
        items={sidebarItems}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
      />

      {/* Main Content */}
      <div className="flex flex-col gap-16 flex-1 lg:max-w-none xl:px-10">
        {/* Header */}
        <section id="ttt-overview-nav" className="flex flex-col gap-4">
          <div className="inline-block">
            <span className="font-black text-sm uppercase tracking-[0.2em] text-[#17A257] bg-[#E7F6EC] px-3 py-1 rounded-full">
              Text-to-Text API
            </span>
          </div>
          <div id="ttt-overview" className="flex flex-col gap-4">
            <h1 className="font-black text-3xl lg:text-4xl leading-tight text-[#1D2129]">
              Quickstart Guide For Text-to-Text API
            </h1>
            <p className="font-medium text-lg leading-relaxed text-[#7E726B]">
              Welcome to the NKENNE AI Translation API. This API allows you to
              translate text between English, Yoruba, Swahili, and Nigerian
              Pidgin using a fast and reliable REST interface.
            </p>
          </div>
        </section>

        {/* Getting Started Section */}
        <div className="flex flex-col gap-20">
          <section id="ttt-getting-started" className="flex flex-col gap-6 w-full">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Getting Started
            </h2>

            <div className="flex flex-col gap-6">
              {/* Base URL */}
              <div id="ttt-base-url" className="flex flex-col gap-2">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Base URL
                </h3>
                <div className="bg-[#F4F8F7] p-3 rounded-xl border border-[#D0E7DF] inline-block w-fit">
                  <p className="font-mono font-medium text-base text-[#1E6C56]">
                    https://developers-api.nkenne.ai
                  </p>
                </div>
              </div>

              {/* Authentication */}
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                    Authentication
                  </h3>
                  <p className="font-['Inter'] font-medium text-base text-[#7E726B]">
                    All API requests require an{" "}
                    <span className="text-[#17A257] font-black">API key</span>{" "}
                    passed in the headers.
                  </p>
                </div>

                {/* How to get API Key */}
                <div id="ttt-get-api-key" className="flex flex-col gap-5">
                  <h4 className="font-['Axiforma'] font-black text-sm uppercase tracking-widest text-[#1D2129] opacity-40">
                    How to get an API Key
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-4">
                    {[
                      "Sign up for a NKENNE AI account",
                      "Navigate to Developer Tools → API Keys in your dashboard",
                      "Generate a new API key",
                      "Copy the key and store it securely in your environment",
                    ].map((step, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#17A257]/10 flex items-center justify-center mt-0.5">
                          <span className="text-[#17A257] font-black text-xs">
                            {index + 1}
                          </span>
                        </div>
                        <p className="font-medium text-sm text-[#1D2129]">
                          {step}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Authentication Header */}
              <div id="ttt-auth-header" className="flex flex-col gap-2">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Authentication Header
                </h3>
                <div className="bg-[#1D2129] p-4 rounded-xl font-mono text-sm text-white overflow-hidden w-fit">
                  <span className="text-[#BC39B7]">Authorization</span>:{" "}
                  <span className="text-[#17A257]">YOUR_API_KEY</span>
                </div>
              </div>
            </div>
          </section>

          {/* Translation API */}
          <section id="ttt-translation-api" className="flex flex-col gap-6">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Translation API
            </h2>

            <div className="flex flex-col gap-6">
              {/* Translation Endpoint */}
              <div id="ttt-endpoint" className="flex flex-col gap-5">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Endpoint
                </h3>
                <div className="flex items-center gap-3">
                  <MethodBadge method="POST" />
                  <span className="font-mono text-sm font-medium text-[#1D2129]">
                    /api/translate/with-api-key
                  </span>
                </div>
              </div>

              {/* Full Endpoint URL */}
              <div id="ttt-full-endpoint-url" className="flex flex-col gap-3">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Full Endpoint URL
                </h3>
                <p className="font-mono text-sm font-medium text-[#1E6C56] bg-[#F4F8F7] p-3 rounded-xl border border-[#D0E7DF] inline-block w-fit">
                  https://developers-api.nkenne.ai/api/translate/with-api-key
                </p>
              </div>

              {/* Request Header */}
              <div id="ttt-request-header" className="flex flex-col gap-4">
                <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                  Request Header
                </h3>
                <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base leading-[1.693em] text-[#000000]">
                          Header
                        </th>
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base leading-[1.693em] text-[#000000]">
                          Required
                        </th>
                        <th className="text-left p-3 font-['Axiforma'] font-semibold text-base leading-[1.693em] text-[#000000]">
                          Description
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-[#E0E4E2]">
                        <td className="p-3 font-['Inter'] font-normal text-base leading-[1.21em] text-[#000000]">
                          <Keyword>Authorization</Keyword>
                        </td>
                        <td className="p-3">
                          <Check className="w-5 h-5 text-[#17A257]" />
                        </td>
                        <td className="p-3 font-['Inter'] font-normal text-base leading-[1.21em] text-[#000000]">
                          Your NKENNE AI API key
                        </td>
                      </tr>
                      <tr>
                        <td className="p-3 font-['Inter'] font-normal text-base leading-[1.21em] text-[#000000]">
                          <Keyword>Content-Type</Keyword>
                        </td>
                        <td className="p-3">
                          <Check className="w-5 h-5 text-[#17A257]" />
                        </td>
                        <td className="p-3 font-['Inter'] font-normal text-base leading-[1.21em] text-[#000000]">
                          <Keyword color="#0070C1">application/json</Keyword>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Request Body (Payload) */}
              <div id="ttt-request-body" className="flex flex-col gap-10">
                <div className="flex flex-col gap-4">
                  <div className="flex flex-col gap-2">
                    <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-[#000000]">
                      Request Body (Payload)
                    </h3>
                    <p className="font-['Inter'] font-normal text-base leading-[1.21em] text-[#000000]">
                      The request body must be sent as a valid JSON object:
                    </p>
                  </div>

                  {/* General Structure */}
                  <div className="flex flex-col gap-4">
                    <p className="font-['Axiforma'] font-medium text-base leading-[1.693em] text-[#000000]">
                      General Structure
                    </p>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <Keyword color="#BC39B7">text</Keyword>
                      <Keyword color="#BC39B7">fromLanguage</Keyword>
                      <Keyword color="#BC39B7">toLanguage</Keyword>
                    </div>
                    <CodeBlock
                      id="ttt-general-structure"
                      language="json"
                      code={`{
  "text": "string",
  "fromLanguage": "Language",
  "toLanguage": "Language"
}`}
                    />
                  </div>

                  {/* Payload Examples By Language Pairs */}
                  <div id="ttt-payload-examples" className="flex flex-col gap-10">
                    <h3 className="font-['Axiforma'] font-medium text-lg leading-[1.693em] text-[#000000]">
                      Payload Examples By Language Pairs
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {[
                        {
                          title: "English → Yoruba",
                          code: `{
  "text": "I am working on my app",
  "fromLanguage": "English",
  "toLanguage": "Yoruba"
}`,
                        },
                        {
                          title: "English → Swahili",
                          code: `{
  "text": "I am working on my app",
  "fromLanguage": "English",
  "toLanguage": "Swahili"
}`,
                        },
                        {
                          title: "English → Nigerian Pidgin",
                          code: `{
  "text": "I am working on my app",
  "fromLanguage": "English",
  "toLanguage": "Nigerian Pidgin"
}`,
                        },
                        {
                          title: "Yoruba → English",
                          code: `{
  "text": "Mo n ṣiṣẹ lori app mi",
  "fromLanguage": "Yoruba",
  "toLanguage": "English"
}`,
                        },
                        {
                          title: "Swahili → English",
                          code: `{
  "text": "Ninafanya kazi kwenye programu yangu",
  "fromLanguage": "Swahili",
  "toLanguage": "English"
}`,
                        },
                        {
                          title: "Nigerian Pidgin → English",
                          code: `{
  "text": "I dey work for my app",
  "fromLanguage": "Nigerian Pidgin",
  "toLanguage": "English"
}`,
                        },
                      ].map((example, index) => (
                        <div key={index} className="flex flex-col gap-3">
                          <p className="font-['Axiforma'] font-bold text-sm text-[#1D2129]">
                            {example.title}
                          </p>
                          <CodeBlock
                            id={`ttt-payload-${index}`}
                            language="json"
                            code={example.code}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Language Reference */}
          <section id="ttt-language-reference" className="flex flex-col gap-8">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Language Reference
            </h2>

            <div className="flex flex-col gap-10">
              {/* Supported Language Values */}
              <div id="ttt-supported-values" className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <h3 className="font-['Axiforma'] font-black text-lg leading-[1.693em] text-left text-[#000000]">
                    Supported Language Values
                  </h3>
                  <p className="font-['Inter'] font-normal text-base leading-[1.21em] text-[#000000]">
                    Use exactly the following string values:
                  </p>
                </div>
                <div className="border border-[#E0E4E2] rounded-lg overflow-hidden w-fit">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                        <th className="text-left p-3 font-['Axiforma'] font-bold text-base text-[#000000]">
                          Language
                        </th>
                        <th className="text-left p-3 font-['Axiforma'] font-bold text-base text-[#000000]">
                          Value
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { lang: "English", value: '"English"' },
                        { lang: "Yoruba", value: '"Yoruba"' },
                        { lang: "Swahili", value: '"Swahili"' },
                        { lang: "Nigerian Pidgin", value: '"Nigerian Pidgin"' },
                      ].map((row, index) => (
                        <tr
                          key={index}
                          className={index < 3 ? "border-b border-[#E0E4E2]" : ""}
                        >
                          <td className="p-3 font-medium text-base text-[#000000]">
                            {row.lang}
                          </td>
                          <td className="p-3 font-mono text-sm text-[#17A257]">
                            {row.value}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Language Pair Rules */}
              <div id="ttt-pair-rules" className="flex flex-col gap-4">
                <h3 className="font-black text-lg text-[#000000]">
                  Language Pair Rules
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#F9FAFB] p-5 rounded-2xl border border-[#E0E4E2]">
                  <div>
                    <p className="font-bold text-sm text-[#1D2129] mb-2">
                      When <Keyword color="#BC39B7">fromLanguage</Keyword> is English:
                    </p>
                    <p className="text-sm text-[#7E726B]">
                      You can translate to <span className="font-semibold text-black">Yoruba</span>, <span className="font-semibold text-black">Swahili</span>, or <span className="font-semibold text-black">Nigerian Pidgin</span>.
                    </p>
                  </div>
                  <div>
                    <p className="font-bold text-sm text-[#1D2129] mb-2">
                      When <Keyword color="#BC39B7">toLanguage</Keyword> is English:
                    </p>
                    <p className="text-sm text-[#7E726B]">
                      The <Keyword color="#BC39B7">fromLanguage</Keyword> must be <span className="font-semibold text-black">Yoruba</span>, <span className="font-semibold text-black">Swahili</span>, or <span className="font-semibold text-black">Nigerian Pidgin</span>.
                    </p>
                  </div>
                </div>
              </div>

              {/* Valid Language Pairs */}
              <div id="ttt-valid-pairs" className="flex flex-col gap-4">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Valid Language Pairs
                </h3>
                <div className="border border-[#E0E4E2] rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#E0E4E2] bg-gray-50/50">
                        <th className="text-left p-3 font-['Axiforma'] font-bold text-base text-[#000000]">
                          From
                        </th>
                        <th className="text-right p-3 font-['Axiforma'] font-bold text-base text-[#000000]">
                          To
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        { from: "English", to: "Yoruba" },
                        { from: "English", to: "Swahili" },
                        { from: "English", to: "Nigerian Pidgin" },
                        { from: "Yoruba", to: "English" },
                        { from: "Swahili", to: "English" },
                        { from: "Nigerian Pidgin", to: "English" },
                      ].map((row, index) => (
                        <tr
                          key={index}
                          className={index < 5 ? "border-b border-[#E0E4E2]" : ""}
                        >
                          <td className="p-3 font-medium text-sm text-[#000000]">
                            {row.from}
                          </td>
                          <td className="p-3 text-right font-medium text-sm text-[#000000]">
                            {row.to}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="font-medium text-sm text-[#E11D48]">
                  Note: Translating between the same language (e.g. English → English) or direct non-English pairs (e.g. Yoruba → Swahili) is not currently supported.
                </p>
              </div>
            </div>
          </section>

          {/* Code Examples */}
          <section id="ttt-code-examples" className="flex flex-col gap-8">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Code Examples
            </h2>

            <div className="flex flex-col gap-8">
              {/* Python Example */}
              <div id="ttt-python" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-lg text-[#000000]">
                  Python
                </p>
                <CodeBlock
                  id="ttt-python-example"
                  language="python"
                  code={`import requests

API_URL = "https://developers-api.nkenne.ai/api/translate/with-api-key"
API_KEY = "your_api_key_here"

payload = {
    "text": "I am working on my app",
    "fromLanguage": "English",
    "toLanguage": "Yoruba"
}

headers = {
    "Authorization": API_KEY,
    "Content-Type": "application/json"
}

response = requests.post(API_URL, headers=headers, json=payload)
response.raise_for_status()

data = response.json()
print("Translated text:", data.get("translatedText"))`}
                />
              </div>

              {/* cURL Example */}
              <div id="ttt-curl" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  cURL
                </p>
                <CodeBlock
                  id="ttt-curl-example"
                  language="bash"
                  code={`curl -X POST https://developers-api.nkenne.ai/api/translate/with-api-key \\
  -H "Authorization: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "text": "I am working on my app",
    "fromLanguage": "English",
    "toLanguage": "Yoruba"
  }'`}
                />
              </div>

              {/* JavaScript Example */}
              <div id="ttt-javascript" className="flex flex-col gap-3">
                <p className="font-['Axiforma'] font-bold text-base text-[#000000]">
                  JavaScript (Fetch)
                </p>
                <CodeBlock
                  id="ttt-javascript-example"
                  language="javascript"
                  code={`const response = await fetch(
  "https://developers-api.nkenne.ai/api/translate/with-api-key",
  {
    method: "POST",
    headers: {
      "Authorization": "YOUR_API_KEY",
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      text: "I am working on my app",
      fromLanguage: "English",
      toLanguage: "Yoruba"
    })
  }
);

const data = await response.json();
console.log("Translated text:", data.translatedText);`}
                />
              </div>
            </div>
          </section>

          {/* Response Format */}
          <section id="ttt-response-format" className="flex flex-col gap-8">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Response Format
            </h2>

            <div className="flex flex-col gap-10">
              {/* Success Response */}
              <div id="ttt-success-response" className="flex flex-col gap-3">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Success Response (200 OK)
                </h3>
                <CodeBlock
                  id="ttt-success-response-code"
                  language="json"
                  code={`{
  "translatedText": "Mo n ṣiṣẹ lori app mi"
}`}
                />
              </div>

              {/* Error Responses */}
              <div id="ttt-error-responses" className="flex flex-col gap-6">
                <h3 className="font-['Axiforma'] font-black text-lg text-[#000000]">
                  Error Responses
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Invalid API Key */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Invalid API Key (401)
                    </p>
                    <CodeBlock
                      id="ttt-error-401"
                      language="json"
                      code={`{
  "error": "Unauthorized"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      Occurs when the API key is missing or invalid. Check that your key is active in Developer Tools.
                    </p>
                  </div>

                  {/* Invalid Language Pair */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Invalid Language Pair (400)
                    </p>
                    <CodeBlock
                      id="ttt-error-400-pair"
                      language="json"
                      code={`{
  "error": "Invalid language combination"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      Occurs when the specified combination is not supported. Ensure one language is English.
                    </p>
                  </div>

                  {/* Missing Fields */}
                  <div className="flex flex-col gap-3 p-5 rounded-2xl border border-red-100 bg-red-50/20">
                    <p className="font-bold text-sm text-[#E11D48]">
                      Missing Fields (400)
                    </p>
                    <CodeBlock
                      id="ttt-error-400-missing"
                      language="json"
                      code={`{
  "error": "text, fromLanguage and toLanguage are required"
}`}
                    />
                    <p className="text-xs text-[#7E726B] leading-relaxed">
                      Occurs when required payload fields are omitted. Include all three fields in your JSON body.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Best Practices */}
          <section id="ttt-best-practices" className="flex flex-col gap-6">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Best Practices
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { title: "Validate Language Pairs", desc: "Always confirm source and target languages comply with supported rules before sending requests." },
                { title: "Secure API Keys", desc: "Never expose your API key in client-side code or public repositories. Use environment variables on secure servers." },
                { title: "Handle Errors Gracefully", desc: "Implement retry logic with exponential backoff and handle 401/400 HTTP errors gracefully." },
                { title: "Monitor Usage Limits", desc: "Track your token consumption in Developer Tools → Usage Analytics to prevent interruptions." },
              ].map((practice, index) => (
                <div key={index} className="p-4 rounded-xl border border-[#E0E4E2] bg-white">
                  <h4 className="font-bold text-base text-[#1D2129] mb-1">{practice.title}</h4>
                  <p className="text-sm text-[#7E726B]">{practice.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Next Steps */}
          <section id="ttt-next-steps" className="flex flex-col gap-6 mb-12">
            <h2 className="font-black text-3xl text-[#1D2129] border-b border-[#E0E4E2] pb-4">
              Next Steps
            </h2>
            <div className="flex flex-col gap-3">
              {[
                "Explore higher rate limits and larger token allocations by upgrading your plan",
                "Integrate text translations into web, mobile, or backend applications",
                "Combine with Text-to-Speech API to build end-to-end voice translation pipelines",
                "Contact NKENNE AI for custom domain models and enterprise agreements",
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-2 h-2 rounded-full bg-[#17A257]" />
                  <p className="font-medium text-base text-[#1D2129]">{step}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TextToTextTab;
